import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { v4 } from "uuid";
import $ from "jquery";
import { GeaoContext } from '../../../GeaoContextProvider';
import { GdkMultipleSelectBox } from "@gdk/multiple-select-box";
import useYesNoOptions from "../../../../../hooks/useYesNoOptions";
import { classes } from "../../../../../util/style-utils";
import PrincipalFlowVerticalStepperElements from "../../../constants/PrincipalFlowVerticalStepperElements";
import GeaoVerticalStepperLayout from "../../shared/Layout/GeaoVerticalStepperLayout";
import { LANGUAGE_OPTIONS } from "@geicoap/ia-shared";
import useTitle from "../../../../../hooks/useTitle";
import useMappers from "./useMappers";
import { validationSchema } from "./validation";
import useInterviewUpdater from "../../../hooks/useInterviewUpdater";
import useNavigation from "./useNavigation";
import useFormButtons from "../../../hooks/useFormButtons";

export default function PrincipalAgencyAdditionalInformation() {
    useTitle("Additional Information");

    const { interviewIsCompleted } = useContext(GeaoContext);

    const [formData, setFormData] = useState();

    const { defaultValues, agencyAnswers } = useMappers(formData);

    const { register, handleSubmit, watch, setValue, trigger, getValues, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: defaultValues,
    });

    const {
        pageStatus,
        exitAfterSave,
        onSubmit,
        onSaveAndExit
    } = useFormButtons(setFormData, trigger, getValues);

    const activityRequest = exitAfterSave ? {
        ActivityCategory: "GEAO_ONBOARDING",
        ActivitySubType: "SAVE_AND_EXIT",
        SourceApplication: "AGIWUI",
        AdditionalData: {
            ComponentName: "PrincipalAgencyAdditionalInformation"
        }
    } : null;

    const {
        updatingInterviews,
        interviewsUpdated
    } = useInterviewUpdater(
        "PrincipalAgencyAdditionalInformation",
        pageStatus,
        null,
        agencyAnswers,
        activityRequest
    );

    useNavigation(interviewsUpdated, exitAfterSave);

    const managementSystemOptions = ManagementSystemOptions();
    const comparativeRaterOptions = ComparativeRaterOptions();
    const yesNoOptions = useYesNoOptions();

    useEffect(() => {
        if (LANGUAGE_OPTIONS) {
            new GdkMultipleSelectBox({ content: "#language-selections" });
            $("language-selections").trigger("change");
            $("select[name=languageSelections]").on("change", handleLanguageChange);
        }
    }, [LANGUAGE_OPTIONS]);

    function handleLanguageChange() {
        const languageSelections = GetLanguageSelections();
        setValue("languageSelections", languageSelections);
    }

    $("#associations").on("change", function () {
        const associations = GetAssociations() || [];
        setValue("associations", associations);
    });

    function GetAssociations() {
        return $("#associations input[type=checkbox]:checked").toArray().map(e => e.value) || [];
    }

    const watchAssociations = watch("associations") || [];
    const noneSelected = watchAssociations.includes("NOT");
    const otherSelected = watchAssociations.length > 0 && !noneSelected;

    const watchManagementSystem = watch("managementSystem");
    const watchComparativeRater = watch("comparativeRater");
    const watchIvansDownload = watch("acceptsAndUsesIvansDownload");
    const watchLanguageSelections = watch("languageSelections");

    return (
        <GeaoVerticalStepperLayout
            verticalStepperElements={PrincipalFlowVerticalStepperElements}
            currentStepperItem="Additional Information"
            header="Additional Information"
        >
            <form className="form geao-form" onSubmit={handleSubmit(onSubmit)}>
                <style>
                    {`
                        .geao-form .uppercase-input { text-transform: uppercase }
                        .geao-form .uppercase-input::placeholder { text-transform: none }
                    `}
                </style>

                {LANGUAGE_OPTIONS &&
                    <fieldset>
                        <div className={classes("form-field", errors.languageSelections && "form-field--error")}>
                            <label htmlFor="language-selections" className="text">
                                Languages Offered for Customers Besides English
                            </label>
                            <span className="form-descriptive-copy">Select all options that apply.</span>
                            <div>
                                <select multiple id="language-selections" data-placeholder="Select Languages" {...register("languageSelections")}>
                                    <option value=""></option>
                                    {LANGUAGE_OPTIONS?.filter(option => option.value !== "ENG").map(option => (
                                        <option value={option.value} key={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                            {errors.languageSelections &&
                                <span className="form-message">{errors.languageSelections.message}</span>
                            }
                        </div>

                        {watchLanguageSelections?.includes("OTHER") &&
                            <div className={classes("form-field", errors.otherLanguageSelections && "form-field--error")} style={{ width: "34rem" }}>
                                <label htmlFor="other-language-selections" className="text">Please Specify Language</label>
                                <input type="text" id="other-language-selections"
                                    {...register("otherLanguageSelections")} />
                                {errors.otherLanguageSelections &&
                                    <span className="form-message">{errors.otherLanguageSelections.message}</span>
                                }
                            </div>
                        }
                    </fieldset>
                }

                <div className="flex-row">
                    <div className={classes("form-field", errors.managementSystem && "form-field--error")}>
                        <label htmlFor="management-system" className="text">
                            Management System Used{/*
                            */}<span className='required'>*</span>
                        </label>
                        <div className="select-box">
                            <select id="management-system" {...register("managementSystem")}>
                                <option value="">Please Select</option>
                                {managementSystemOptions?.map(option => (
                                    <option value={option.value} key={option.value}>{option.text}</option>
                                ))}
                            </select>
                        </div>
                        {errors.managementSystem &&
                            <span className="form-message">{errors.managementSystem.message}</span>
                        }
                    </div>

                    {watchManagementSystem === "OTHER" && (
                        <div className={classes("form-field", errors.otherManagementSystem && "form-field--error")}>
                            <label htmlFor="other-management-system" className="text">
                                Name of Other Management System Used{/*
                                */}<span className='required'>*</span>
                            </label>
                            <input type="text" id="other-management-system"
                                {...register("otherManagementSystem")} />
                            {errors.otherManagementSystem &&
                                <span className="form-message">{errors.otherManagementSystem.message}</span>
                            }
                        </div>
                    )}
                </div>

                <div className="flex-row">
                    <div className={classes("form-field", errors.comparativeRater && "form-field--error")}>
                        <label htmlFor="comparative-rater" className="text">
                            Comparative Rater Used{/*
                            */}<span className='required'>*</span>
                        </label>
                        <div className="select-box">
                            <select id="comparative-rater" {...register("comparativeRater")}>
                                <option value="">Please Select</option>
                                {comparativeRaterOptions?.map(option => (
                                    <option value={option.value} key={option.value}>{option.text}</option>
                                ))}
                            </select>
                        </div>
                        {errors.comparativeRater && (
                            <span className="form-message">{errors.comparativeRater.message}</span>
                        )}
                    </div>

                    {watchComparativeRater === "OTHER" &&
                        <div className={classes("form-field", errors.otherComparativeRater && "form-field--error")}>
                            <label htmlFor="other-comparative-rater" className="text">
                                Name of Other Comparative Rater Used{/*
                                */}<span className='required'>*</span>
                            </label>
                            <input type="text" id="other-comparative-rater"
                                {...register("otherComparativeRater")} />
                            {errors.otherComparativeRater &&
                                <span className="form-message">{errors.otherComparativeRater.message}</span>
                            }
                        </div>
                    }
                </div>

                <fieldset role="radiogroup" className={classes("form-field radio-button-group", errors.acceptsAndUsesIvansDownload && "form-field--error")}>
                    <legend>
                        Does your agency accept and use Ivans Download?{/*
                        */}<span className='required'>*</span>
                    </legend>
                    <div className="radio-button-wrapper col-2">
                        {yesNoOptions?.map((option) => {
                            const id = `${option.label}-radio-${v4()}`;
                            return (
                                <div key={id} className="col-width-fix">
                                    <input
                                        id={id}
                                        type="radio"
                                        value={option.value}
                                        {...register("acceptsAndUsesIvansDownload")}
                                    />
                                    <label className="radio" htmlFor={id}>
                                        <span>{option.label}</span>
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                    {errors.acceptsAndUsesIvansDownload &&
                        <span className="form-message">{errors.acceptsAndUsesIvansDownload.message}</span>
                    }
                </fieldset>

                {watchIvansDownload === "true" && (
                    <div className="flex-row">
                        <div className={classes("form-field", errors.ivansAccountId && "form-field--error")}>
                            <label htmlFor="ivans-account-id" className="text">
                                Ivans Account ID{/*
                                */}<span className='required'>*</span>
                            </label>
                            <input type="text" id="ivans-account-id" placeholder="Y Code" className="uppercase-input"
                                {...register("ivansAccountId")} />
                            {errors.ivansAccountId &&
                                <span className="form-message">{errors.ivansAccountId.message}</span>
                            }
                        </div>

                        <div className={classes("form-field", errors.ivansUserId && "form-field--error")}>
                            <label htmlFor="ivans-user-id" className="text">Ivans User ID</label>
                            <input type="text" id="ivans-user-id" placeholder="Agency Mailbox" className="uppercase-input"
                                {...register("ivansUserId")} />
                            {errors.ivansUserId &&
                                <span className="form-message">{errors.ivansUserId.message}</span>
                            }
                        </div>
                    </div>
                )}

                <fieldset role="radiogroup" id="associations" className={classes("form-field checkbox-group", errors.associations && "form-field--error")}>
                    <legend>
                        Do you belong to any of the following associations?{/*
                        */}<span className='required'>*</span>
                    </legend>
                    <span className="form-descriptive-copy">Select all options that apply.</span>

                    <div className="checkbox-wrapper col-1">
                        <div className="checkbox-wrapper">
                            <input
                                id="association-iiaba"
                                value="IIABA"
                                type="checkbox"
                                {...register("associations")}
                                disabled={noneSelected}
                            />
                            <label htmlFor="association-iiaba" className="checkbox">
                                <span>IIABA</span>
                            </label>
                        </div>
                        <div className="checkbox-wrapper">
                            <input
                                id="association-pia"
                                value="PIA"
                                type="checkbox"
                                {...register("associations")}
                                disabled={noneSelected}
                            />
                            <label htmlFor="association-pia" className="checkbox">
                                <span>National Association of Professional Insurance Agents</span>
                            </label>
                        </div>
                        <div className="checkbox-wrapper">
                            <input
                                id="association-sia"
                                value="SIA"
                                type="checkbox"
                                {...register("associations")}
                                disabled={noneSelected}
                            />
                            <label htmlFor="association-sia" className="checkbox">
                                <span>State Insurance Association</span>
                            </label>
                        </div>
                        <div className="checkbox-wrapper">
                            <input
                                id="association-none"
                                value="NOT"
                                type="checkbox"
                                {...register("associations")}
                                disabled={otherSelected}
                            />
                            <label htmlFor="association-none" className="checkbox">
                                <span>None</span>
                            </label>
                        </div>
                        <div className="checkbox-wrapper">
                            <input
                                id="association-other"
                                value="OTHER"
                                type="checkbox"
                                {...register("associations")}
                                disabled={noneSelected}
                            />
                            <label htmlFor="association-other" className="checkbox">
                                <span>Other</span>
                            </label>
                        </div>
                        {errors.associations &&
                            <span className="form-message">{errors.associations.message}</span>
                        }
                    </div>
                </fieldset>

                <p>
                    <span className='required'>*</span> Indicates required field.
                </p>

                <button
                    id="next-button"
                    type="submit"
                    className="btn btn--primary btn--full-mobile btn--pull-right"
                    disabled={updatingInterviews || interviewIsCompleted}
                >
                    <div>Next</div>
                </button>
                <button
                    id="save-and-exit-button"
                    type="button"
                    className="btn btn--secondary btn--full-mobile btn--pull-right"
                    onClick={() => {
                        handleSubmit(onSaveAndExit)();
                    }}
                    disabled={updatingInterviews || interviewIsCompleted}
                >
                    <div>Save and Exit</div>
                </button>
            </form>
        </GeaoVerticalStepperLayout>
    );
}

const ManagementSystemOptions = () => [
    { value: "AMS", text: "AMS 360" },
    { value: "CAT", text: "Catalyst" },
    { value: "EPC", text: "Epic" },
    { value: "EZL", text: "EZLynx" },
    { value: "HWK", text: "HawkSoft" },
    { value: "NOTUSE", text: "Do Not Use" },
    { value: "OTHER", text: "Other" }
];

const ComparativeRaterOptions = () => [
    { value: "DRC", text: "DRC" },
    { value: "EZL", text: "EZLynx" },
    { value: "PLRATING", text: "PL Rating" },
    { value: "SF", text: "SureFyre" },
    { value: "TURBO", text: "TurboRater" },
    { value: "VER", text: "Verity" },
    { value: "NOTUSE", text: "Do Not Use" },
    { value: "OTHER", text: "Other" }
];

const GetLanguageSelections = () => $("select[name=languageSelections]").select2("data").map(d => d.id);